import { memo, useCallback } from "react"
import { cn } from "@/lib/utils"
import { ClipView } from "./clip-view"
import { clipVersionId, isAnimaticTrack, trackVolume } from "./timeline-model"
import type { TimelineAudioAsset, TimelineSource, TimelineTrack } from "./types"

type Props = {
  track: TimelineTrack
  pxPerMs: number
  selectedClipIds: readonly string[]
  /** Клипы, замороженные активными lipsync-джобами. */
  lockedClipIds: ReadonlySet<string>
  sources: Record<string, TimelineSource>
  /** Filmstrip исходника аниматика (общий на шот). */
  animaticSource?: TimelineSource | null
  assets: TimelineAudioAsset[]
  onSelectClip: (trackId: string, clipId: string, additive: boolean) => void
  onMoveClip: (trackId: string, clipId: string, newStartMs: number) => void
  onResizeClip: (trackId: string, clipId: string, edge: "left" | "right", newEdgeMs: number) => void
  onRateStretchClip?: (trackId: string, clipId: string, edge: "left" | "right", newEdgeMs: number) => void
  onFadeClip?: (trackId: string, clipId: string, edge: "in" | "out", fadeMs: number) => void
  onDragEnd: () => void
  /** Клик по correction-клипу: открыть модалку корректировки. */
  onActivateCorrectionClip?: (trackId: string, clipId: string) => void
  activateCorrectionLabel?: string
}

// Единая высота: шапки дорожек имеют одинаковый двухстрочный макет.
const LANE_HEIGHT: Record<TimelineTrack["kind"], string> = {
  video: "h-12",
  version_audio: "h-12",
  custom_audio: "h-12",
}

export const TrackLane = memo(function TrackLane({
  track,
  pxPerMs,
  selectedClipIds,
  lockedClipIds,
  sources,
  animaticSource = null,
  assets,
  onSelectClip,
  onMoveClip,
  onResizeClip,
  onRateStretchClip,
  onFadeClip,
  onDragEnd,
  onActivateCorrectionClip,
  activateCorrectionLabel,
}: Props) {
  const isCorrection = track.purpose === "correction"
  const handleSelect = useCallback(
    (clipId: string, additive: boolean) => onSelectClip(track.id, clipId, additive),
    [onSelectClip, track.id],
  )
  const handleMove = useCallback(
    (clipId: string, newStartMs: number) => onMoveClip(track.id, clipId, newStartMs),
    [onMoveClip, track.id],
  )
  const handleResize = useCallback(
    (clipId: string, edge: "left" | "right", newEdgeMs: number) => onResizeClip(track.id, clipId, edge, newEdgeMs),
    [onResizeClip, track.id],
  )
  const handleRateStretch = useCallback(
    (clipId: string, edge: "left" | "right", newEdgeMs: number) =>
      onRateStretchClip?.(track.id, clipId, edge, newEdgeMs),
    [onRateStretchClip, track.id],
  )
  const handleFade = useCallback(
    (clipId: string, edge: "in" | "out", fadeMs: number) => onFadeClip?.(track.id, clipId, edge, fadeMs),
    [onFadeClip, track.id],
  )
  const handleActivate = useCallback(
    (clipId: string) => onActivateCorrectionClip?.(track.id, clipId),
    [onActivateCorrectionClip, track.id],
  )

  return (
    <div
      className={cn(
        "relative min-w-0 flex-1 overflow-hidden rounded-md",
        "bg-ui-surface-sunken/60",
        LANE_HEIGHT[track.kind],
      )}
    >
      {track.clips.map((clip) => {
        const asset = clip.audio_asset_id != null ? (assets.find((a) => a.id === clip.audio_asset_id) ?? null) : null
        // Источник per-clip: у lipsync-клипов свой override версии.
        const versionId = track.kind !== "custom_audio" ? clipVersionId(track, clip) : null
        const source = isAnimaticTrack(track)
          ? (animaticSource ?? null)
          : versionId != null
            ? (sources[String(versionId)] ?? null)
            : null
        return (
          <ClipView
            key={clip.id}
            clip={clip}
            trackKind={track.kind}
            pxPerMs={pxPerMs}
            selected={selectedClipIds.includes(clip.id)}
            // Блеклость: у видео — только скрытый «глазом» слой (mute — это лишь звук,
            // его показывает гистограмма); у аудио — mute гасит клип целиком.
            muted={track.kind === "video" ? track.hidden === true : track.muted || track.hidden === true}
            volumeGain={track.muted || track.hidden === true ? 0 : trackVolume(track)}
            videoWaveformGain={
              track.kind === "video" &&
              !track.audio_detached &&
              track.animatic_mode !== "video_silent" &&
              track.hidden !== true
                ? track.muted
                  ? 0
                  : trackVolume(track)
                : null
            }
            locked={lockedClipIds.has(clip.id)}
            moveDisabled={isCorrection}
            source={source}
            onActivateClip={isCorrection && onActivateCorrectionClip ? handleActivate : undefined}
            activateLabel={isCorrection ? activateCorrectionLabel : undefined}
            assetName={asset?.name ?? null}
            waveform={asset?.waveform ?? source?.waveform ?? null}
            onSelect={handleSelect}
            onMove={handleMove}
            onResize={handleResize}
            onRateStretch={onRateStretchClip ? handleRateStretch : undefined}
            onFade={track.kind !== "video" && onFadeClip ? handleFade : undefined}
            onDragEnd={onDragEnd}
          />
        )
      })}
    </div>
  )
})
